import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import PnLTable from "@/components/PnLTable";
import BalanceSheetTable from "@/components/BalanceSheetTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  ArrowLeft, ArrowClockwise, MagnifyingGlass, Warning,
  Calendar, ChartPie, Stack, Ruler, House, Buildings,
  CurrencyDollar, Wallet, Coins, TrendUp, CaretDown,
  ArrowsOutSimple, ArrowsInSimple, Clock, FilePdf, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import {
  PIE_COLORS, SECTION_KEYS, VIEW_OPTIONS,
  ViewTabs, NoSource, MetaItem, KpiBlock, SubMetric,
  SpendToDateCard, GrossRevenueCard, ProfitProjectionCard, StatusPill,
  RevenueTableBody, VendorTableBody, CollapsibleSection, MoneyTooltip,
  ExportShareModal,
  toTitle, EmptyChart, renderPieLabel, renderBudgetLabel, renderSpentLabel,
} from "@/components/project-summary";

export default function ProjectSummaryPage({ dashboardMode = false } = {}) {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Contract / Closing-statement PDFs are shared across businesses by unit
  // number (uploading on Harbor View LLC unit 13 also surfaces on Building B
  // unit 13). Anyone management + above can upload.
  const canEditDocs = !!user?.is_super_admin || user?.role === "admin" || user?.role === "secondary_admin";
  const canDeleteDocs = !!user?.is_super_admin;
  const [unitDocs, setUnitDocs] = useState({}); // "<unit>|<kind>" -> doc
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get("view") || "summary").toLowerCase();
  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "summary") next.delete("view"); else next.set("view", v);
    setSearchParams(next, { replace: true });
  };

  // In dashboardMode we maintain our own selected business (from /businesses list).
  // Otherwise the businessId comes from the route param.
  const [businesses, setBusinesses] = useState([]);
  const [selectedBizId, setSelectedBizId] = useState(params.id || "");
  useEffect(() => {
    if (!dashboardMode) return;
    let cancelled = false;
    api.get("/businesses").then(({ data }) => {
      if (cancelled) return;
      setBusinesses(data);
      if (!selectedBizId && data.length > 0) setSelectedBizId(data[0].id);
    }).catch(() => {});
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [dashboardMode]);

  const id = dashboardMode ? selectedBizId : params.id;

  // Trim trailing "00:00:00" / time portion off sheet-supplied dates and
  // present as MM/DD/YYYY (e.g. "10/01/2025") so it matches the original
  // Harbor View Equities LLC layout.
  const fmtMetaDate = (v) => {
    if (!v) return "—";
    const s = String(v).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
    const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (us) {
      const yyyy = us[3].length === 2 ? `20${us[3]}` : us[3];
      return `${us[1].padStart(2, "0")}/${us[2].padStart(2, "0")}/${yyyy}`;
    }
    return s;
  };

  // Compute duration from start + end dates as "X.YZ yrs" (or "X mo" for <1y).
  const computeDuration = (start, end) => {
    if (!start || !end) return "—";
    const parseAny = (v) => {
      const s = String(v).trim();
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
      const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (us) {
        const yyyy = us[3].length === 2 ? 2000 + Number(us[3]) : Number(us[3]);
        return new Date(yyyy, Number(us[1]) - 1, Number(us[2]));
      }
      return null;
    };
    const sd = parseAny(start);
    const ed = parseAny(end);
    if (!sd || !ed || Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return "—";
    const days = (ed - sd) / 86400000;
    if (days <= 0) return "—";
    const years = days / 365.25;
    if (years < 1) return `${Math.round(years * 12)} mo`;
    return `${years.toFixed(2)} yrs`;
  };

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [fetchedAt, setFetchedAt] = useState(null);
  const [lastDataDate, setLastDataDate] = useState(null);
  const [data, setData] = useState(null);
  const [configured, setConfigured] = useState(true);
  const [error, setError] = useState("");

  // Overview (used for PNL + Balance Sheet views)
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Collapsible sections (Expenses + Revenue collapsed by default)
  const [expanded, setExpanded] = useState({
    spend: true,
    comparison: true,
    expenses: false,
    revenue: false,
    vendor: false,
  });
  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));
  const expandAll = () => setExpanded(Object.fromEntries(SECTION_KEYS.map((k) => [k, true])));
  const collapseAll = () => setExpanded(Object.fromEntries(SECTION_KEYS.map((k) => [k, false])));

  // Expenses table — Total column sort (null = no sort, desc = highest first, asc = lowest first)
  const [expenseSort, setExpenseSort] = useState(null);
  const cycleExpenseSort = () => setExpenseSort((s) => (s === null ? "desc" : s === "desc" ? "asc" : null));

  // Vendor summary state
  const [vendorData, setVendorData] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendorError, setVendorError] = useState("");

  // Export / share modal state
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMode, setExportMode] = useState("pdf"); // "pdf" | "share"
  const [shareMode, setShareMode] = useState("live"); // "live" | "snapshot"
  const [sectionPick, setSectionPick] = useState({
    summary: true, kpis: true, "gross-revenue": true, profit: true, spend: true,
    comparison: true, expenses: true, vendor: true, revenue: true,
  });
  const [generatedShareUrl, setGeneratedShareUrl] = useState("");
  const exportRootRef = useRef(null);

  const revenueRef = useRef(null);

  const load = async (refresh = false) => {
    if (!id) { setLoading(false); return; }
    if (refresh) setRefreshing(true); else setLoading(true);
    setError("");
    try {
      const { data: resp } = await api.get(`/businesses/${id}/project-summary`, {
        params: refresh ? { refresh: true } : {},
      });
      setConfigured(resp.configured !== false);
      setData(resp.data || null);
      setBusinessName(resp.business_name || "");
      setFetchedAt(resp.fetched_at || null);
      setLastDataDate(resp.last_data_date || null);
      if (refresh) toast.success("Project summary refreshed");
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (msg) {
        setError(msg);
        if (refresh) toast.error(msg);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadOverview = async (refresh = false) => {
    if (!id) return;
    setOverviewLoading(true);
    setOverviewError("");
    try {
      if (refresh) {
        await api.post(`/businesses/${id}/refresh`);
      }
      const { data: resp } = await api.get(`/businesses/${id}/overview`);
      setOverview(resp);
      // Also keep business name in sync if we haven't loaded summary yet
      if (resp.business?.name) setBusinessName(resp.business.name);
      if (refresh) toast.success("Data refreshed");
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (msg) {
        setOverviewError(msg);
        if (refresh) toast.error(msg);
      }
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    if (view === "summary") {
      load(false);
    } else {
      // Skip the heavy project summary fetch — just need the business name + transaction date for header
      setLoading(false);
      api.get(`/businesses/${id}/project-summary`).then(({ data: resp }) => {
        if (resp?.business_name) setBusinessName(resp.business_name);
        if (resp?.last_data_date) setLastDataDate(resp.last_data_date);
        if (resp?.fetched_at) setFetchedAt(resp.fetched_at);
      }).catch(() => {});
      if (!overview) loadOverview(false);
    }
    // Fire-and-forget activity log
    api.post(`/businesses/${id}/log-view`, { view }).catch(() => {});
    /* eslint-disable-next-line */
  }, [id, view]);

  // Load Contract / Closing-statement docs once on mount — they are shared by
  // unit number across all businesses, so we don't refetch on biz switch.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/unit-documents").then(({ data }) => {
      if (cancelled) return;
      const map = {};
      (data || []).forEach((d) => { map[`${d.unit_number}|${d.kind}`] = d; });
      setUnitDocs(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  // Lazy-load vendor summary the first time the Vendor section is expanded
  const loadVendor = async (refresh = false) => {
    setVendorLoading(true);
    setVendorError("");
    try {
      const { data: resp } = await api.get(`/businesses/${id}/vendor-summary`, {
        params: refresh ? { refresh: true } : {},
      });
      if (resp.configured === false) {
        setVendorData({ headers: [], items: [], notConfigured: true });
      } else {
        setVendorData(resp.data);
      }
    } catch (e) {
      const msg = apiErrorMessage(e);
      if (msg) setVendorError(msg);
    } finally {
      setVendorLoading(false);
    }
  };

  useEffect(() => {
    if (expanded.vendor && !vendorData && !vendorLoading) {
      loadVendor(false);
    }
    /* eslint-disable-next-line */
  }, [expanded.vendor]);

  const categories = useMemo(() => data?.categories || [], [data]);
  const items = useMemo(() => data?.items || [], [data]);
  const revenueItems = useMemo(() => data?.revenue_items || [], [data]);

  // Exclude "Revenue" class type rows from the Expenses table & charts (they're handled separately)
  const expenseItems = useMemo(
    () => items.filter((it) => (it.class_type || "").trim().toLowerCase() !== "revenue"),
    [items]
  );

  const categoryOptions = useMemo(() => {
    const set = new Set(expenseItems.map((i) => i.class_type).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [expenseItems]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = expenseItems.filter((it) => {
      const matchSearch = !q
        || it.name?.toLowerCase().includes(q)
        || it.class_type?.toLowerCase().includes(q)
        || it.sub_class_type?.toLowerCase().includes(q);
      const matchCat = categoryFilter === "all" || it.class_type === categoryFilter;
      return matchSearch && matchCat;
    });
    if (expenseSort === "desc") return [...list].sort((a, b) => (b.total || 0) - (a.total || 0));
    if (expenseSort === "asc")  return [...list].sort((a, b) => (a.total || 0) - (b.total || 0));
    return list;
  }, [expenseItems, search, categoryFilter, expenseSort]);

  // Build chart data, filtered to non-revenue. Filter/search drives the data.
  const expenseCategories = useMemo(
    () => (categories || []).filter((c) => (c.name || "").trim().toLowerCase() !== "revenue"),
    [categories]
  );

  const filteredCategoriesChart = useMemo(() => {
    if (search.trim() || categoryFilter !== "all") {
      const map = {};
      for (const it of filteredItems) {
        const k = it.class_type || "Uncategorized";
        if (!map[k]) map[k] = { name: k, paid: 0, budget: 0, total: 0 };
        map[k].paid += it.paid || 0;
        map[k].budget += it.budget || 0;
        map[k].total += it.total || 0;
      }
      return Object.values(map).sort((a, b) => b.total - a.total);
    }
    return expenseCategories;
  }, [expenseCategories, filteredItems, search, categoryFilter]);

  if (loading && view === "summary") {
    return (
      <div className="min-h-screen app-bg">
        <AppHeader />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ps-skeleton">
          {/* Title row skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-8 w-24" />
          </div>
          {/* KPI cards skeleton — matches 3-col grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-2 w-full" />
                <div className="flex gap-2">
                  <Skeleton className="h-2 w-1/3" />
                  <Skeleton className="h-2 w-1/3" />
                </div>
              </div>
            ))}
          </div>
          {/* Chart skeleton */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4 space-y-3">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-48 w-full" />
          </div>
          {/* Table skeleton */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
            <Skeleton className="h-4 w-32" />
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="grid grid-cols-4 gap-3">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (view === "summary" && (!configured || error || !data)) {
    return (
      <div className="min-h-screen app-bg">
        <AppHeader />
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {!dashboardMode && (
            <button onClick={() => navigate("/businesses")} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-4">
              <ArrowLeft size={14} /> Back to businesses
            </button>
          )}
          <ViewTabs view={view} onChange={setView} />
          <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center mt-4">
            <Warning size={28} className="text-amber-500 mx-auto" />
            <h2 className="mt-3 font-display text-xl font-bold text-slate-900">
              {error ? "Couldn't load project summary" : "No Project Summary sheet connected"}
            </h2>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              {error || "Add the Project Summary sheet URL (pointing to the SummaryA tab) in the business settings."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Link to="/businesses">
                <Button variant="outline" className="border-slate-300">Manage business</Button>
              </Link>
              {configured && !error && (
                <Button onClick={() => load(true)} className="bg-[#327de3] hover:bg-[#1f64c2] text-white">
                  <ArrowClockwise size={16} className="mr-1.5" /> Try again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const project = data?.project || {};
  const kpis = data?.kpis || {};

  const scrollToRevenue = () => {
    setExpanded((s) => ({ ...s, revenue: true }));
    setTimeout(() => revenueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  return (
    <div className="min-h-screen app-bg">
      <AppHeader />

      {/* Sub-header */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {dashboardMode ? (
              <div className="flex items-center gap-2 min-w-0">
                <Buildings size={18} weight="bold" className="text-[#1f64c2] shrink-0" />
                {businesses.length === 0 ? (
                  <span className="text-sm text-slate-500">No businesses available</span>
                ) : (
                  <Select value={selectedBizId} onValueChange={(v) => { setSelectedBizId(v); setOverview(null); setData(null); }}>
                    <SelectTrigger className="w-[200px] sm:w-[280px] h-9 sm:h-10 border-slate-300" data-testid="business-select-trigger">
                      <SelectValue placeholder="Select a business" />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map((b) => (
                        <SelectItem key={b.id} value={b.id} data-testid={`business-option-${b.id}`}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ) : (
              <>
                <button onClick={() => navigate("/businesses")} className="text-slate-500 hover:text-slate-800" data-testid="back-to-businesses">
                  <ArrowLeft size={18} />
                </button>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {VIEW_OPTIONS.find((v) => v.key === view)?.label || "Summary"}
                  </div>
                  <div className="font-display text-sm sm:text-lg font-semibold text-slate-900 truncate" data-testid="project-summary-business-name">{businessName}</div>
                </div>
              </>
            )}

            {/* View tabs — slightly smaller than the dropdown, blue, separated visually */}
            <div className="ml-2 sm:ml-4 hidden sm:flex">
              <ViewTabs view={view} onChange={setView} />
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {(fetchedAt || lastDataDate) && view === "summary" && (
              <div className="hidden lg:flex flex-col items-end leading-tight" data-testid="header-update-stack">
                {fetchedAt && (
                  <span className="text-xs text-slate-500" data-testid="header-updated-at">Updated {new Date(fetchedAt).toLocaleString()}</span>
                )}
                {lastDataDate && (
                  <span className="text-[11px] text-slate-500" data-testid="header-transaction-data-date">
                    Transaction Data Last Updated:{" "}
                    <span className="font-semibold text-slate-700">
                      {new Date(lastDataDate + "T00:00:00").toLocaleDateString()}
                    </span>
                  </span>
                )}
              </div>
            )}
            <Button
              onClick={() => view === "summary" ? load(true) : loadOverview(true)}
              disabled={refreshing || overviewLoading}
              variant="outline"
              className="h-9 sm:h-10 px-2 sm:px-3 border-slate-300"
              data-testid="ps-refresh-button"
            >
              <ArrowClockwise size={16} className={`${(refreshing || overviewLoading) ? "animate-spin" : ""} sm:mr-1.5`} />
              <span className="hidden sm:inline">{(refreshing || overviewLoading) ? "Refreshing…" : "Refresh"}</span>
            </Button>
            {view === "summary" && (
              <>
                <Button onClick={() => { setExportMode("pdf"); setGeneratedShareUrl(""); setExportOpen(true); }} variant="outline" className="h-9 sm:h-10 px-2 sm:px-3 border-slate-300" data-testid="export-pdf-button">
                  <FilePdf size={16} className="sm:mr-1.5" />
                  <span className="hidden sm:inline">Export PDF</span>
                </Button>
                <Button onClick={() => { setExportMode("share"); setGeneratedShareUrl(""); setExportOpen(true); }} variant="outline" className="h-9 sm:h-10 px-2 sm:px-3 border-slate-300" data-testid="share-link-button">
                  <LinkSimple size={16} className="sm:mr-1.5" />
                  <span className="hidden sm:inline">Share link</span>
                </Button>
              </>
            )}
          </div>
        </div>
        {/* Mobile tabs row when too narrow */}
        <div className="sm:hidden px-4 pb-3 -mt-1">
          <ViewTabs view={view} onChange={setView} />
        </div>
      </div>

      {/* Render PNL or Balance Sheet view */}
      {view !== "summary" && (
        <main className="max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-3">
          {overviewLoading ? (
            <div className="text-sm text-slate-500">Loading {view === "pnl" ? "profit & loss" : "balance sheet"}…</div>
          ) : overviewError ? (
            <NoSource title={view === "pnl" ? "P&L" : "Balance Sheet"} error={overviewError} />
          ) : view === "pnl" ? (
            overview?.pnl ? <PnLTable data={overview.pnl} /> : <NoSource title="P&L" />
          ) : (
            overview?.balance ? <BalanceSheetTable data={overview.balance} /> : <NoSource title="Balance Sheet" />
          )}
        </main>
      )}

      {/* Render Project Summary view (the heavy default) */}
      {view === "summary" && (
      <main className="max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-3" ref={exportRootRef}>
        {/* Project meta */}
        <section data-section="summary" className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            <MetaItem icon={<Calendar size={16} weight="bold" />} label="Start date" value={fmtMetaDate(project.start_date)} testId="meta-start-date" />
            <MetaItem icon={<Calendar size={16} weight="bold" />} label="Projected end date" value={fmtMetaDate(project.projected_end_date)} testId="meta-end-date" />
            <MetaItem icon={<Clock size={16} weight="bold" />} label="Duration" value={project.duration_years ? `${project.duration_years.toFixed(2)} yrs` : "—"} testId="meta-duration" />
            <MetaItem icon={<House size={16} weight="bold" />} label="Total units" value={project.unit_count ? project.unit_count.toLocaleString() : "—"} testId="meta-unit-count" />
            <MetaItem icon={<Ruler size={16} weight="bold" />} label="Total square feet" value={project.square_feet ? `${project.square_feet.toLocaleString()} SF` : "—"} testId="meta-square-feet" />
            <MetaItem icon={<Stack size={16} weight="bold" />} label="Avg SF / unit" value={project.avg_sf_per_unit ? `${Math.round(project.avg_sf_per_unit).toLocaleString()} SF` : "—"} testId="meta-avg-sf" />
          </div>
        </section>

        {/* KPI cards — 3 cards */}
        <section data-section="kpis" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          <SpendToDateCard metric={kpis.spend_to_date} project={project} testId="kpi-spend-to-date" />
          <KpiBlock
            label="Total Remaining to Spend"
            sublabel="(Budget Minus Paid)"
            metric={kpis.total_remaining}
            accent="#7C3AED"
            icon={<Coins size={18} weight="bold" />}
            testId="kpi-total-remaining"
          />
          <KpiBlock
            label="Total Budgeted"
            metric={kpis.total_budgeted}
            accent="#1f64c2"
            icon={<CurrencyDollar size={18} weight="bold" />}
            testId="kpi-total-budgeted"
          />
        </section>

        {/* Gross Revenue — full width */}
        <div data-section="gross-revenue">
          <GrossRevenueCard metric={kpis.gross_proceeds} project={project} onClick={scrollToRevenue} testId="kpi-gross-revenue" />
        </div>

        {/* Profit Projection — full width 5th card */}
        <div data-section="profit">
          <ProfitProjectionCard
            spendToDate={kpis.spend_to_date}
            totalBudgeted={kpis.total_budgeted}
            grossProceeds={kpis.gross_proceeds}
            project={project}
          />
        </div>

        {/* Expand all / Collapse all — two wide cards spanning full width */}
        <section data-section-controls className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={expandAll}
            className="bg-white rounded-xl border border-slate-200 shadow-sm hover:border-[#327de3] hover:shadow transition-all px-6 py-5 inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#327de3]"
            data-testid="expand-all-button"
          >
            <ArrowsOutSimple size={18} weight="bold" /> Expand all sections
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="bg-white rounded-xl border border-slate-200 shadow-sm hover:border-[#327de3] hover:shadow transition-all px-6 py-5 inline-flex items-center justify-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#327de3]"
            data-testid="collapse-all-button"
          >
            <ArrowsInSimple size={18} weight="bold" /> Collapse all sections
          </button>
        </section>

        {/* Spend by Category (donut) */}
        <div data-section="spend">
        <CollapsibleSection
          title="Spend by Category"
          icon={<ChartPie size={16} weight="bold" />}
          expanded={expanded.spend}
          onToggle={() => toggle("spend")}
          testId="section-spend"
        >
          <div className="p-6">
            {filteredCategoriesChart.filter((c) => Math.abs(c.total) > 0).length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie
                    data={filteredCategoriesChart.filter((c) => Math.abs(c.total) > 0)}
                    dataKey="total"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="78%"
                    innerRadius="45%"
                    paddingAngle={2}
                    labelLine={{ stroke: "#94a3b8", strokeWidth: 1 }}
                    label={(entry) => `${(entry.percent * 100).toFixed(1)}%`}
                    isAnimationActive={false}
                  >
                    {filteredCategoriesChart.filter((c) => Math.abs(c.total) > 0).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<MoneyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </CollapsibleSection>
        </div>

        {/* Budget vs Spent by Category (bar) */}
        <div data-section="comparison">
        <CollapsibleSection
          title="Budget vs Spent by Category"
          icon={<Buildings size={16} weight="bold" />}
          expanded={expanded.comparison}
          onToggle={() => toggle("comparison")}
          testId="section-comparison"
        >
          <div className="p-6">
            {filteredCategoriesChart.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={filteredCategoriesChart} margin={{ left: 0, right: 8, top: 40, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6A78" }} angle={-25} textAnchor="end" height={70} interval={0} />
                  <YAxis tick={{ fontSize: 11, fill: "#5E6A78" }} tickFormatter={(v) => formatCurrency(v, { compact: true })} />
                  <Tooltip content={<MoneyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="budget" name="Budget" fill="#CBD5E1" radius={[4, 4, 0, 0]} label={renderBudgetLabel} />
                  <Bar dataKey="total" name="Spent" fill="#327de3" radius={[4, 4, 0, 0]} label={renderSpentLabel(filteredCategoriesChart)} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CollapsibleSection>
        </div>

        {/* Expenses (was Line Items) */}
        <div data-section="expenses">
        <CollapsibleSection
          title="Expenses"
          subtitle={`${filteredItems.length} of ${expenseItems.length}`}
          icon={<Wallet size={16} weight="bold" />}
          expanded={expanded.expenses}
          onToggle={() => toggle("expenses")}
          testId="section-expenses"
        >
          <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
            <div className="relative w-full sm:w-72">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search categories…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10"
                data-testid="ps-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-10 w-full sm:w-52 border-slate-300" data-testid="ps-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt === "all" ? "All class types" : opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[10px] sm:text-sm">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr>
                  <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Category</th>
                  <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider hidden md:table-cell">Class Type</th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Budget</th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Paid</th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Payables</th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">
                    <button
                      type="button"
                      onClick={cycleExpenseSort}
                      className={`inline-flex items-center gap-0.5 sm:gap-1 hover:text-slate-700 ${expenseSort ? "text-slate-800" : ""}`}
                      data-testid="expense-total-sort"
                    >
                      Total
                      <CaretDown
                        size={11}
                        weight="bold"
                        className={`transition-transform ${expenseSort ? "" : "opacity-30"} ${expenseSort === "asc" ? "rotate-180" : ""}`}
                      />
                    </button>
                  </th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider hidden xl:table-cell">Per Unit</th>
                  <th className="px-1 sm:px-5 py-2 text-right text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider hidden xl:table-cell">Per SF</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-1 sm:px-5 py-6 text-center text-sm text-slate-400">No matching line items.</td>
                  </tr>
                ) : filteredItems.map((it, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`ps-item-row-${idx}`}>
                    <td className="px-1 sm:px-5 py-[5px] text-slate-800 font-medium">{it.name}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-slate-500 text-[10px] sm:text-xs hidden md:table-cell">{it.class_type}{it.sub_class_type ? ` · ${toTitle(it.sub_class_type)}` : ""}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-700">{formatCurrency(it.budget)}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-900 font-medium">{formatCurrency(it.paid)}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-700">{formatCurrency(it.unpaid)}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-900 font-semibold">{formatCurrency(it.total)}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-600 hidden xl:table-cell">{formatCurrency(it.per_unit)}</td>
                    <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-600 hidden xl:table-cell">{formatCurrency(it.per_sf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
        </div>

        {/* Expenses by Vendor */}
        <div data-section="vendor">
          <CollapsibleSection
            title="Expenses by Vendor"
            subtitle={vendorData?.items ? `${vendorData.items.length} vendors` : ""}
            icon={<Buildings size={16} weight="bold" />}
            expanded={expanded.vendor}
            onToggle={() => toggle("vendor")}
            testId="section-vendor"
          >
            <VendorTableBody data={vendorData} loading={vendorLoading} error={vendorError} onRetry={() => loadVendor(true)} />
          </CollapsibleSection>
        </div>

        <div data-section="revenue" ref={revenueRef}>
          <CollapsibleSection
            title="Revenue"
            subtitle={`${revenueItems.length} units`}
            icon={<TrendUp size={16} weight="bold" />}
            expanded={expanded.revenue}
            onToggle={() => toggle("revenue")}
            testId="section-revenue"
          >
            <RevenueTableBody
              items={revenueItems}
              unitDocs={unitDocs}
              canEditDocs={canEditDocs}
              canDeleteDocs={canDeleteDocs}
              onDocUploaded={(u, kind, fresh) => setUnitDocs((prev) => {
                const next = { ...prev };
                if (fresh) next[`${u}|${kind}`] = fresh;
                else delete next[`${u}|${kind}`];
                return next;
              })}
            />
          </CollapsibleSection>
        </div>
      </main>
      )}

      <ExportShareModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        mode={exportMode}
        shareMode={shareMode}
        setShareMode={setShareMode}
        sectionPick={sectionPick}
        setSectionPick={setSectionPick}
        businessId={id}
        businessName={businessName}
        lastDataDate={lastDataDate}
        rootRef={exportRootRef}
        generatedShareUrl={generatedShareUrl}
        setGeneratedShareUrl={setGeneratedShareUrl}
        setExpanded={setExpanded}
      />
    </div>
  );
}
